import Papa from 'papaparse';
import { Commune, ItinerairePoint } from '../types/Commune';

export class CommuneService {
  private static communes: Commune[] = [];

  // Service pour obtenir les coordonnées via l'API gouvernementale française
  private static async getCoordinatesFromAPI(codePostal: string, nomCommune: string): Promise<{ lat: number; lng: number } | null> {
    try {
      console.log(`🔍 Recherche coordonnées API pour ${nomCommune} (${codePostal})`);
      
      // API gouvernementale française pour les communes
      const response = await fetch(`https://geo.api.gouv.fr/communes?codePostal=${codePostal}&nom=${encodeURIComponent(nomCommune)}&fields=centre`);
      const data = await response.json();
      
      if (data && data.length > 0 && data[0].centre) {
        const coords = data[0].centre;
        console.log(`✅ Coordonnées API pour ${nomCommune} (${codePostal}):`, coords);
        // L'API retourne [longitude, latitude] dans coordinates
        return { lat: coords.coordinates[1], lng: coords.coordinates[0] };
      }
      
      console.log(`⚠️ Pas de coordonnées API pour ${nomCommune} (${codePostal})`);
      return null;
    } catch (error) {
      console.log(`❌ Erreur API pour ${nomCommune} (${codePostal}):`, error);
      return null;
    }
  }

  // Méthode de fallback avec coordonnées approximatives par département
  private static getFallbackCoordinates(codePostal: string): { lat: number; lng: number } {
    const dept = codePostal.substring(0, 2);
    const deptCoordinates: { [key: string]: { lat: number; lng: number } } = {
      '10': { lat: 48.2973, lng: 4.0744 }, // Aube
      '52': { lat: 48.1113, lng: 5.1419 }, // Haute-Marne
      '55': { lat: 48.7694, lng: 5.3654 }, // Meuse
      '54': { lat: 48.6844, lng: 6.1844 }, // Meurthe-et-Moselle
      '89': { lat: 47.7986, lng: 3.5738 }, // Yonne
    };

    const coords = deptCoordinates[dept] || { lat: 46.603354, lng: 1.888334 }; // Centre de la France par défaut
    
    // Ajouter un petit offset aléatoire pour éviter la superposition
    const offsetLat = (Math.random() - 0.5) * 0.1; // ±0.05 degrés
    const offsetLng = (Math.random() - 0.5) * 0.1; // ±0.05 degrés
    
    const finalCoords = {
      lat: coords.lat + offsetLat,
      lng: coords.lng + offsetLng
    };
    
    console.log(`🔄 Utilisation des coordonnées de fallback pour ${codePostal} (département ${dept}):`, finalCoords);
    return finalCoords;
  }



  static async loadCommunes(): Promise<Commune[]> {
    if (this.communes.length > 0) {
      return this.communes;
    }

    try {
      const response = await fetch('/communes1.csv');
      const csvText = await response.text();
      
      const result = Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        delimiter: ';',
        transform: (value, field) => {
          if (field === 'Nombre d\'habitants') {
            return parseInt(value) || 0;
          }
          return value;
        }
      });
      
      console.log('✅ CSV parsé avec', result.data.length, 'lignes');

      let logCount = 0;
      this.communes = await Promise.all(result.data.map(async (row: any) => {
        const codePostal = row['Adresse du Siège - Code Postal '] || '';
        const nomCommune = row['Nom '] || '';
        const ville = row['Adresse du Siège - Ville '] || '';
        
              // Utiliser l'API gouvernementale pour obtenir les vraies coordonnées
      console.log(`🔍 Tentative API pour: ${ville} (${codePostal})`);
      let coordinates = await this.getCoordinatesFromAPI(codePostal, ville);
      
      // Si l'API ne trouve pas avec le nom de la ville, essayer avec le nom de la commune
      if (!coordinates && nomCommune !== ville) {
        console.log(`🔍 Tentative API avec nom commune: ${nomCommune} (${codePostal})`);
        coordinates = await this.getCoordinatesFromAPI(codePostal, nomCommune);
      }
      
      // Si toujours pas de coordonnées, utiliser le fallback
      if (!coordinates) {
        coordinates = this.getFallbackCoordinates(codePostal);
      }
        
        const commune = {
          id: row['ID'] || '',
          nom: nomCommune,
          nombreHabitants: row['Nombre d\'habitants'] || 0,
          poleCommercial: row['Pôle commercial'] || '',
          statut: row['Statut'] || '',
          departement: row['Adresse du Siège - Département'] || '',
          codePostal: codePostal,
          ville: ville,
          telephone: row['Téléphone '] || '',
          email: row['Adresse Email'] || '',
          canton: row['Canton'] || '',
          horairesOuverture: row['Horaires d\'ouverture'] || '',
          structureCliente: row['Structure cliente de'] || '',
          structureOrigine: row['Structure d\'origine'] || '',
          latitude: coordinates?.lat,
          longitude: coordinates?.lng
        } as Commune;
        
        // Log seulement pour les premières communes pour éviter le spam
        if (logCount < 5) {
          if (coordinates) {
            console.log(`✅ Coordonnées pour ${commune.nom} (${codePostal}):`, coordinates);
          } else {
            console.log(`⚠️ Pas de coordonnées pour ${commune.nom} (code postal: ${codePostal})`);
          }
          logCount++;
        }
        
        return commune;
      }));

      return this.communes;
    } catch (error) {
      console.error('Erreur lors du chargement des communes:', error);
      return [];
    }
  }

  static getCommuneById(id: string): Commune | undefined {
    return this.communes.find(commune => commune.id === id);
  }

  static calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Rayon de la Terre en km
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  private static deg2rad(deg: number): number {
    return deg * (Math.PI/180);
  }

  static findCommunesWithinRadius(communeCentrale: Commune, radiusKm: number = 50): Commune[] {
    console.log('🔍 findCommunesWithinRadius appelé pour', communeCentrale.nom);
    console.log('🔍 Coordonnées de la commune centrale:', communeCentrale.latitude, communeCentrale.longitude);
    
    if (!communeCentrale.latitude || !communeCentrale.longitude) {
      console.log('⚠️ Pas de coordonnées pour la commune centrale');
      return [communeCentrale];
    }

    const communesInRadius = this.communes.filter(commune => {
      if (commune.id === communeCentrale.id) return true;
      if (!commune.latitude || !commune.longitude) return false;
      
      // À ce point, TypeScript sait que les coordonnées existent
      const distance = this.calculateDistance(
        communeCentrale.latitude as number, communeCentrale.longitude as number,
        commune.latitude as number, commune.longitude as number
      );
      return distance <= radiusKm;
    });
    
    console.log('✅ Trouvé', communesInRadius.length, 'communes dans le rayon de', radiusKm, 'km');
    
    // Log des communes trouvées pour debug
    communesInRadius.slice(0, 5).forEach((c, i) => {
      console.log(`🔍 Commune dans rayon ${i}: ${c.nom} - coords: [${c.latitude}, ${c.longitude}]`);
    });
    
    if (communesInRadius.length <= 1) {
      console.log('⚠️ Attention: seulement', communesInRadius.length, 'commune(s) trouvée(s). L\'itinéraire sera court.');
    }
    return communesInRadius;
  }

  static calculateOptimalItinerary(communes: Commune[]): ItinerairePoint[] {
    console.log('🔍 calculateOptimalItinerary appelé avec', communes.length, 'communes');
    
    if (communes.length === 0) return [];

    // Filtrer les communes avec des coordonnées valides
    const communesWithCoordinates = communes.filter(c => c.latitude && c.longitude);
    console.log('🔍 Communes avec coordonnées:', communesWithCoordinates.length);
    console.log('🔍 Total des communes reçues:', communes.length);
    
    // Log des premières communes pour debug
    communes.slice(0, 5).forEach((c, i) => {
      console.log(`🔍 Commune ${i}: ${c.nom} - coords: [${c.latitude}, ${c.longitude}]`);
    });
    
    if (communesWithCoordinates.length === 0) {
      console.log('⚠️ Aucune commune avec coordonnées valides - problème de mapping des codes postaux');
    }
    
    if (communesWithCoordinates.length === 0) {
      console.log('⚠️ Aucune commune avec coordonnées valides');
      return communes.map((commune, index) => ({
        commune,
        distance: 0,
        ordre: index
      }));
    }

    // Algorithme du plus proche voisin pour un itinéraire approximatif
    const unvisited = [...communesWithCoordinates];
    const itinerary: ItinerairePoint[] = [];
    
    // Commencer par la première commune
    let current = unvisited.shift()!;
    itinerary.push({
      commune: current,
      distance: 0,
      ordre: 0
    });

    let ordre = 1;
    const MAX_ETAPES = 20; // Limite à 20 étapes maximum

    while (unvisited.length > 0 && itinerary.length < MAX_ETAPES) {
      let nearestIndex = 0;
      let minDistance = Infinity;

      // Trouver la commune la plus proche
      for (let i = 0; i < unvisited.length; i++) {
        const distance = this.calculateDistance(
          current.latitude!, current.longitude!,
          unvisited[i].latitude!, unvisited[i].longitude!
        );
        if (distance < minDistance) {
          minDistance = distance;
          nearestIndex = i;
        }
      }

      const nextCommune = unvisited.splice(nearestIndex, 1)[0];
      
      itinerary.push({
        commune: nextCommune,
        distance: minDistance,
        ordre: ordre++
      });

      current = nextCommune;
    }

    console.log('✅ Itinéraire calculé avec', itinerary.length, 'étapes');
    console.log('🔍 Détail de l\'itinéraire calculé:', itinerary.map(p => ({ nom: p.commune.nom, ordre: p.ordre })));
    return itinerary;
  }
} 